import { Navigate, useParams } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { requestBase } from '@/lib/drafts';
import { skuDisplay } from '@/components/SkuInfo';
import { RequestForm, type DraftSeed, type ReviseSeed } from './RequestForm';
import type { RequestKind } from '@/api/types';

/** ISO instant -> the seller's local calendar day (the form stores the day as local midday / end of day). */
function localDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
const whole = (q: string): string => String(Math.round(Number(q)));

type Loaded = { readonly seed: DraftSeed; readonly revise?: ReviseSeed } | 'NOT_ALLOWED';

/**
 * Edit a DRAFT (the default) or - with `revise` - a request that is already at the warehouse (ADR-089): the same form as
 * a new request, seeded from the saved request. A draft can be edited only while it was never sent; a sent request only
 * where the server says it may still be changed (`can_revise`). Anything else goes back to the request's page (the
 * server refuses the edit as well).
 */
export function RequestEditPage({ kind, revise = false }: { kind: RequestKind; revise?: boolean }): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const result = useApi<Loaded>(async () => {
    if (kind === 'OUTBOUND') {
      const { order, lines } = await endpoints.order(id!);
      const mode = order.can_revise ?? 'NONE';
      if (revise ? !order.requested_in_portal || mode === 'NONE' : order.state !== 'DRAFT' || !order.requested_in_portal) return 'NOT_ALLOWED';
      const s = order.ship_to ?? {};
      return {
        seed: {
          id: order.id,
          ownerId: order.owner_id ?? '',
          warehouseId: order.warehouse_id ?? '',
          reference: order.reference ?? '',
          date: localDate(order.due_at),
          fulfilment: order.fulfilment_model === 'STANDARD' || order.fulfilment_model === 'FBS' ? order.fulfilment_model : '',
          shipTo: { name: s.name ?? '', phone: s.phone ?? '', address: s.address ?? '', city: s.city ?? '', postalCode: s.postalCode ?? '', country: s.country ?? '', comment: s.comment ?? '' },
          lines: lines.filter((l) => !l.cancelled_at).map((l) => ({ skuId: l.sku_id, code: l.sku_code, name: l.sku_name, qty: whole(l.qty_ordered), ...skuDisplay(l) })),
        },
        ...(revise ? { revise: { revisionNo: order.revision_no ?? 1, mode: mode === 'HEADER' ? 'HEADER' : 'FULL' } as ReviseSeed } : {}),
      };
    }
    const { inbound, lines } = await endpoints.inboundRequest(id!);
    const mode = inbound.can_revise ?? 'NONE';
    if (revise ? !inbound.requested_in_portal || mode === 'NONE' : inbound.state !== 'DRAFT' || !inbound.requested_in_portal) return 'NOT_ALLOWED';
    return {
      seed: {
        id: inbound.id,
        ownerId: inbound.owner_id ?? '',
        warehouseId: inbound.warehouse_id ?? '',
        reference: inbound.reference ?? '',
        date: localDate(inbound.expected_at),
        shipTo: { name: '', address: '', city: '', country: '' },
        // lines the warehouse added without a product (unidentified) are not the seller's to edit
        lines: lines.filter((l) => l.sku_id).map((l) => ({ skuId: l.sku_id ?? '', code: l.sku_code ?? '', name: l.sku_name ?? '', qty: whole(l.qty_expected), ...skuDisplay(l) })),
      },
      ...(revise ? { revise: { revisionNo: inbound.revision_no ?? 1, mode: 'FULL' } as ReviseSeed } : {}),
    };
  }, [id, kind, revise]);

  if (result.status === 'loading') return <LoadingState />;
  if (result.status === 'error') return <ErrorBanner error={result.error} onRetry={result.reload} />;
  if (result.data === 'NOT_ALLOWED') return <Navigate to={`${requestBase(kind)}/${id}`} replace />;
  return <RequestForm kind={kind} draft={result.data.seed} {...(result.data.revise ? { revise: result.data.revise } : {})} />;
}
